import React, { createContext, useContext, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress} from '@material-ui/core';
import { saveUW, savePostAcqUW, setTransientValuation,setTransientValuationType,
  setValuationTypeModalOptions, afterSaveUWNavigate, savePostAcqModuleUW, afterSavePostAcqUWNavigate
} from 'store/actions';
import { selectedProformaInputsSelector } from 'store/selectors';
import { GetValuationTypeMetadata } from "api/revue/underwriteData";
import { Constants } from 'utilities/constants';

const SaveUWDialogContext = createContext();

const SaveUWDialogProvider = ({
  children,
  history,
  shouldCalculateProforma,
  hasInputLimitError,
  isSavingUW,
  selectedOpportunity,
  selectedProformaInputs,
  transientValuation,
  setTransientValuationType,
  setValuationTypeModalOptions,
  saveUW,
  savePostAcqUW,
  afterSaveUWNavigate,
  userHasUnderwriteReadOnlyRole,
  isPostAcqModule,
  viewUnderwriteModalIsOpen,
  savePostAcqModuleUW,
  afterSavePostAcqUWNavigate
 }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [saveUWDialogParameters, setSaveUWDialogParameters] = useState(null);
  const [callback, setCallback] = useState(null);
  const [title, setTitle] = useState('');

  const showDiscard = isPostAcqModule ? Object.keys(hasInputLimitError).some(key => hasInputLimitError[key] === true) || viewUnderwriteModalIsOpen : shouldCalculateProforma;
  const defaultTitle = showDiscard ? 'Discard changes?' : 'Save underwrite?'
  const defaultMessage = showDiscard
    ? `${isPostAcqModule ? 'Unsaved' : 'Uncalculated'} changes will be lost. Do you want to continue?`
    : 'You have unsaved changes. Do you want to save?'

  const openSaveUWDialog = ({ message, title, callback, saveUWDialogParameters } = {}) => {
    setMessage(message ?? defaultMessage);
    setTitle(title ?? defaultTitle);
    saveUWDialogParameters && setSaveUWDialogParameters(saveUWDialogParameters);
    callback && setCallback(() => callback);
    setOpen(true);
  };

  const closeSaveUWDialog = () => {
    setOpen(false);
    setMessage('');
    setTitle('');
    setSaveUWDialogParameters(null);
    setCallback(null);
  };

  const handleDiscard = () => {
    if (saveUWDialogParameters) {
      const afterSaveAction = isPostAcqModule ? afterSavePostAcqUWNavigate : afterSaveUWNavigate;
      afterSaveAction({ history, route: saveUWDialogParameters.route, selectedOpportunity, viewOpp: saveUWDialogParameters.viewOpp, resetTransient: true });
    }
    callback && callback();
  };

  const handleSave = async () => {
    const afterNavigation = saveUWDialogParameters ? { route: saveUWDialogParameters.route, selectedOpportunity, viewOpp: saveUWDialogParameters.viewOpp } : null;
    if (isPostAcqModule) {
      const saveUWPayload = {
        taskId: selectedOpportunity.id,
        transientValuation,
        history,
        afterNavigation,
        callback
      }
      savePostAcqModuleUW(saveUWPayload);
    } else if (!selectedProformaInputs.isPostAcquisitionUnderwrite) {
      const { possibleValues, defaultValue } = await GetValuationTypeMetadata(selectedOpportunity.id);

      if (possibleValues.length > 1) {
        setTransientValuationType(defaultValue);
        setValuationTypeModalOptions({ isOpen: true, possibleValues, afterNavigation, callback });
        return;
      }

      const saveUWPayload = {
        defaultValue,
        txnOppId: selectedOpportunity.id,
        proformaInputsContainerId: selectedProformaInputs.id,
        transientValuation,
        history,
        afterNavigation,
        callback
      }

      saveUW(saveUWPayload)
    } else {
      const saveUWPayload = {
        proformaInputsContainerId: selectedProformaInputs.id,
        transientValuation,
        history,
        afterNavigation,
        callback
      }

      savePostAcqUW(saveUWPayload)
    }
  };

  return (
    <SaveUWDialogContext.Provider value={{ openSaveUWDialog, closeSaveUWDialog }}>
      {children}
      {open &&
        <SaveUWDialog
          open={true}
          onClose={closeSaveUWDialog}
          onDiscard={() => {
            handleDiscard();
            closeSaveUWDialog();
          }}
          onSave={() => {
            handleSave();
            closeSaveUWDialog();
          }}
          message={message}
          title={title}
          showDiscard={showDiscard}
          isSavingUW={isSavingUW}
          userHasUnderwriteReadOnlyRole={userHasUnderwriteReadOnlyRole}
        />
      }
    </SaveUWDialogContext.Provider>
  );
};

const SaveUWDialog = ({
  open,
  onClose,
  onDiscard,
  onSave,
  title,
  message,
  showDiscard,
  isSavingUW,
  userHasUnderwriteReadOnlyRole,
}) => {
  return (
    <Dialog
      open={open}
      keepMounted
      onClose={onClose}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{ style: { width: 400 } }}
    >
      <DialogTitle id="alert-dialog-slide-title">{title}
      </DialogTitle>
      <DialogContent style={{ minHeight: 48, padding: '0 24px 8px' }}>
        {isSavingUW
          ? <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
          : <DialogContentText id="alert-dialog-slide-description">
            {message}
          </DialogContentText>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant='outlined' disabled={isSavingUW}>
          Cancel
        </Button>
        <Button onClick={onDiscard} color="primary" variant='contained' disabled={isSavingUW}>
          {showDiscard ? 'Discard and continue' : "Don't Save"}
        </Button>
        {!showDiscard && // Only allow save and continue if calculation not needed
          <Button onClick={onSave} color="primary" variant='contained' disabled={isSavingUW || userHasUnderwriteReadOnlyRole}>
            Save
          </Button>
        }
      </DialogActions>
    </Dialog>
  )
}

const mapStateToProps = (state, ownProps) => {
  const isExploreViewPostAcq = ownProps.location.pathname === Constants.Routes.EXPLORE && state.exploreData.selectedPropertyUnderwrite?.module === "Post-Acq";

  const isPostAcqModule = ownProps.location.pathname.startsWith(Constants.Routes.POSTACQ) || isExploreViewPostAcq;
  const moduleState = isPostAcqModule ? state.postAcqUWState : state.propertyUWState;
  return {
    isPostAcqModule,
    viewUnderwriteModalIsOpen: state.postAcqUWState.viewUnderwriteModalIsOpen,
    userHasUnderwriteReadOnlyRole: state.appState.user.roles.underwriteReadOnly,
    shouldCalculateProforma: state.propertyUWState.shouldCalculateProforma,
    hasInputLimitError: state.postAcqUWState.hasInputLimitError,
    isSavingUW: moduleState.isSavingUW,
    selectedOpportunity: isPostAcqModule ? moduleState.postAcqUWClientTask : moduleState.propertyUWClientTxnOpp,
    selectedProformaInputs: selectedProformaInputsSelector(state),
    transientValuation: moduleState.transientValuation
  }
};

const mapDispatchToProps = {
  saveUW,
  savePostAcqUW,
  setTransientValuation,
  setTransientValuationType,
  setValuationTypeModalOptions,
  afterSaveUWNavigate,
  savePostAcqModuleUW,
  afterSavePostAcqUWNavigate
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(SaveUWDialogProvider);

export const useSaveUWDialog = () => {
  return useContext(SaveUWDialogContext);
}